import { useTranslation } from 'react-i18next';
import { ListOrdered } from 'lucide-react';

import { Loader } from '@/components/common/Loader';
import { getMyLogtoSub } from '@/lib/logto';
import { useGetLeaderboardQuery } from '@/services/qchessApi';
import { classifyQchessError, qchessErrorKey } from '@/services/qchessErrors';
import { cn } from '@/lib/cn';

/**
 * QChess leaderboard — season ranking table (rank, player, points, W/L/D,
 * tournaments). The signed-in user's row is highlighted when resolvable.
 */
export function ChessLeaderboard() {
  const { t } = useTranslation('chess');
  const me = getMyLogtoSub();

  const query = useGetLeaderboardQuery();
  const entries = query.data?.entries ?? [];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <ListOrdered aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold text-fg">{t('leaderboard.title')}</h1>
        </div>
        {query.data?.seasonId && (
          <p className="text-sm text-fg-muted">
            {t('leaderboard.season', { season: query.data.seasonId })}
          </p>
        )}
      </header>

      {query.isLoading ? (
        <Loader />
      ) : query.error ? (
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {t(qchessErrorKey(classifyQchessError(query.error)))}
        </p>
      ) : entries.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-muted">{t('leaderboard.empty')}</p>
      ) : (
        <div className="overflow-hidden rounded-xl bg-bg-secondary">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[11px] uppercase tracking-wide text-fg-muted">
                <th className="px-2 py-2 text-left font-semibold">{t('leaderboard.rank')}</th>
                <th className="px-2 py-2 text-left font-semibold">{t('leaderboard.player')}</th>
                <th className="px-2 py-2 text-right font-semibold">{t('leaderboard.points')}</th>
                <th className="px-2 py-2 text-right font-semibold">{t('leaderboard.record')}</th>
                <th className="px-2 py-2 text-right font-semibold">{t('leaderboard.tournaments')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => {
                const isMe = !!me && row.logtoUserId === me;
                return (
                  <tr
                    key={row.logtoUserId}
                    className={cn(
                      'border-b border-border last:border-0',
                      isMe && 'bg-brand/10',
                    )}
                  >
                    <td className="px-2 py-2 font-mono text-fg-muted">{row.rank}</td>
                    <td className="px-2 py-2">
                      <span className="font-semibold text-fg">{row.lichessUsername}</span>
                      {isMe && (
                        <span className="ml-1 rounded bg-brand/20 px-1 text-[9px] font-bold uppercase text-brand">
                          {t('leaderboard.you')}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-mono font-semibold text-fg">
                      {row.points}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-[11px] text-fg-muted">
                      {row.wins}/{row.losses}/{row.draws}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-fg-muted">
                      {row.tournamentsPlayed}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}
